import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import StaffCard from "./StaffCard";
import { ASSEMBLY_TAGLINE, staffMembers } from "../utils";
import { ConnectWithUsBlock } from "../nav_panel/Footer";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

const STAFF_ENTRANCE_MS = 820;
const STAFF_STAGGER_BASE_MS = 90;
const STAFF_STAGGER_STEP_MS = 160;

/**
 * Staggered fade-up for founder cards: one IntersectionObserver and CSS
 * compositor-friendly transitions (no Framer stagger on this grid).
 * @param {Object} props
 * @param {Array<Object>} props.staffMembers
 * @param {boolean} props.reduceMotion
 * @returns {JSX.Element}
 */
function StaffGrid({ staffMembers, reduceMotion }) {
  const rootRef = useRef(null);
  const [revealed, setRevealed] = useState(() => Boolean(reduceMotion));

  useEffect(() => {
    if (reduceMotion) {
      setRevealed(true);
      return;
    }
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (hit) {
          io.disconnect();
          requestAnimationFrame(() => setRevealed(true));
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px 6% 0px" }
    );
    io.observe(root);
    return () => io.disconnect();
  }, [reduceMotion]);

  return (
    <div ref={rootRef} className="grid items-start gap-8 md:grid-cols-3">
      {staffMembers.map((staff, index) => {
        const delayMs = STAFF_STAGGER_BASE_MS + index * STAFF_STAGGER_STEP_MS;
        const style =
          reduceMotion || revealed
            ? {
                opacity: 1,
                transform: "translate3d(0,0,0)",
              }
            : {
                opacity: 0,
                transform: "translate3d(0,12px,0)",
              };

        const transitionStyle =
          reduceMotion || !revealed
            ? {}
            : {
                transitionProperty: "opacity, transform",
                transitionDuration: `${STAFF_ENTRANCE_MS}ms`,
                transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                transitionDelay: `${delayMs}ms`,
              };

        return (
          <div
            key={staff.name ?? String(index)}
            className="w-full"
            style={{ ...style, ...transitionStyle }}
          >
            <StaffCard staff={staff} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Single glass-style stat for the pre-launch countdown.
 * @param {Object} props
 * @param {string|number} props.value
 * @param {string} props.label
 * @param {number} props.staggerIndex
 * @returns {JSX.Element}
 */
function CountdownUnit({ value, label, staggerIndex }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      initial={
        reduceMotion ? false : { opacity: 0, scale: 0.88, y: 16 }
      }
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        delay: reduceMotion ? 0 : 0.06 * staggerIndex,
        type: "spring",
        stiffness: 420,
        damping: 24,
      }}
      whileHover={
        reduceMotion
          ? undefined
          : {
              scale: 1.07,
              y: -8,
              transition: { type: "spring", stiffness: 420, damping: 16 },
            }
      }
    >
      <Card className="min-w-[120px] border-2 border-hero-countdown-glass-border bg-hero-countdown-glass-bg text-hero-countdown-value shadow-staff-card backdrop-blur-sm sm:min-w-[150px]">
        <CardHeader className="items-center space-y-2 px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8">
          <span className="text-7xl font-extrabold leading-none drop-shadow-hero-countdown-value sm:text-8xl md:text-9xl">
            {value}
          </span>
          <span className="text-base font-semibold uppercase tracking-wider text-hero-countdown-label sm:text-lg md:text-xl">
            {label}
          </span>
        </CardHeader>
      </Card>
    </motion.div>
  );
}

/**
 * Live countdown to launch target.
 * @returns {JSX.Element}
 */
function Countdown() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const targetDate = new Date("2025-12-17T18:00:00");

    const updateCountdown = () => {
      const now = new Date();
      const timeDifference = targetDate - now;

      if (timeDifference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const units = [
    { value: timeLeft.days, label: "days" },
    { value: timeLeft.hours, label: "hours" },
    { value: timeLeft.minutes, label: "minutes" },
    { value: timeLeft.seconds, label: "seconds" },
  ];

  return (
    <div className="flex flex-col justify-center gap-4 font-bold text-hero-countdown-value sm:flex-row sm:gap-6">
      {units.map((u, i) => (
        <CountdownUnit
          key={u.label}
          value={u.value}
          label={u.label}
          staggerIndex={i}
        />
      ))}
    </div>
  );
}

/**
 * Marketing section wrapper. Intentionally static (no scroll-reveal on the
 * section node) so solid backgrounds paint immediately over the fixed map.
 * @param {Object} props
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function RevealSection({ className, children }) {
  return <section className={className}>{children}</section>;
}

/**
 * Marketing home page: hero, vision, product, founders, optional contact, footer.
 * @param {Object} props
 * @param {string|null} [props.userId]
 * @param {boolean} props.isLive
 * @returns {JSX.Element}
 */
export default function HomePage({ userId = 0, isLive }) {
  const reduceMotion = useReducedMotion();

  return (
    <main className="overflow-x-hidden text-text-primary">
      {/* Map is owned by SharedMapLayer in the app shell (persists on /explore). */}
      <div className="relative z-10">
        <section className="relative flex min-h-[90vh] w-full flex-col items-center justify-center gap-8 overflow-x-hidden px-4 py-12 text-center md:gap-10 md:py-16">
          <div className="relative mx-auto w-full max-w-4xl">
          <div className="mx-auto max-w-2xl rounded-2xl border border-hero-glass-border bg-hero-glass-bg px-6 py-8 shadow-staff-card backdrop-blur-sm md:px-10 md:py-10">
            <motion.div
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: reduceMotion ? 0 : 0.11,
                    delayChildren: reduceMotion ? 0 : 0.06,
                  },
                },
              }}
              initial="hidden"
              animate="visible"
            >
              <motion.h1
                variants={{
                  hidden: { opacity: 0, y: 28, scale: 0.94 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: reduceMotion
                      ? { duration: 0.01 }
                      : { type: "spring", stiffness: 200, damping: 22 },
                  },
                }}
                className="mb-6 text-5xl font-bold text-hero-heading md:text-6xl"
              >
                Assembly
              </motion.h1>
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="mb-8 text-xl text-hero-subheading md:text-2xl"
              >
                {ASSEMBLY_TAGLINE}
              </motion.p>
              {isLive ? (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
                    },
                  }}
                  className="flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center"
                >
                  {!userId && (
                    <Button
                      asChild
                      variant="outline"
                      className="h-12 rounded-full border-hero-cta-outline-border bg-hero-cta-outline-bg px-8 font-semibold text-hero-cta-outline-text shadow-lg transition-[transform,box-shadow] duration-200 ease-out hover:bg-hero-cta-outline-bg/90 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-xl active:scale-[0.98]"
                    >
                      <Link to="/login">Login</Link>
                    </Button>
                  )}
                  <Button
                    asChild
                    className="h-12 rounded-full bg-hero-cta-primary px-8 font-semibold text-hero-cta-primary-fg shadow-lg transition-[transform,box-shadow] duration-200 ease-out hover:bg-hero-cta-primary-hover motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-xl active:scale-[0.98]"
                  >
                    <Link to={userId ? "/new-org" : "/create-account"}>
                      {userId ? "Create Organization" : "Create Account"}
                    </Link>
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 24 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
                    },
                  }}
                  className="flex flex-col items-center"
                >
                  <Countdown />
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>

          <div className="relative flex w-full max-w-4xl justify-center px-2">
            <Link
              to="/explore"
              className="group text-base font-semibold text-hero-heading focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent md:text-lg"
            >
              <span className="underline decoration-hero-heading decoration-2 underline-offset-[5px]">
                Explore Map{" "}
                <span
                  aria-hidden
                  className="inline-block translate-y-px text-xl leading-none transition-transform motion-safe:group-hover:translate-x-1"
                >
                  →
                </span>
              </span>
            </Link>
          </div>
        </section>

        <RevealSection className="bg-secondary px-4 py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-4xl font-bold text-text-primary">Our Vision</h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-text-secondary">
            At Assembly, we believe that it is God&apos;s desire for His church
            to operate in unity and to work together as one body.
          </p>
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 26,
            }}
          >
            <Card className="mx-auto max-w-3xl border-border bg-primary/50">
              <CardContent className="p-8">
                <blockquote className="mb-4 text-xl italic leading-relaxed text-text-primary md:text-2xl">
                  &quot;The glory that you have given me I have given to them,
                  that they may be one even as we are one, I in them and you in
                  me, that they may become perfectly one, so that the world may
                  know that you sent me and loved them even as you loved me.&quot;
                </blockquote>
                <cite className="text-lg font-semibold not-italic text-accent">
                  John 17:22-23
                </cite>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </RevealSection>

      <RevealSection className="bg-primary px-4 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold text-text-primary">
              Our Product
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-text-secondary">
              Assembly is a platform that helps pastors and church leaders
              connect with each other and grow together.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {[
              {
                title: "Pastor-to-Pastor Networking",
                body: "Connect with verified pastors from churches worldwide for fellowship, mentorship, and collaboration.",
              },
              {
                title: "Trust & Verification",
                body: "All pastors and churches are verified to ensure authentic connections within the ministry community.",
              },
            ].map((item) => (
              <motion.div
                key={item.title}
                initial={reduceMotion ? false : { opacity: 0, y: 24 }}
                whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                whileHover={
                  reduceMotion
                    ? undefined
                    : {
                        y: -2,
                        transition: {
                          type: "tween",
                          duration: 0.7,
                          ease: [0.33, 1, 0.53, 1],
                        },
                      }
                }
              >
                <Card className="h-full border-border bg-secondary shadow-md transition-shadow duration-700 ease-out hover:shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl text-text-primary">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-text-secondary">
                      {item.body}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Plain section: avoids stacking section-level motion + staff entrance (scroll glitch). */}
      <section className="bg-secondary px-4 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold text-text-primary">
              Meet Our Founders
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-text-secondary">
              A team of passionate leaders committed to strengthening pastoral
              communities through meaningful connections.
            </p>
          </div>
          <StaffGrid staffMembers={staffMembers} reduceMotion={reduceMotion} />
        </div>
      </section>

      {isLive && (
        <RevealSection className="bg-primary px-4 py-16">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl font-bold text-text-primary">
              Contact Us
            </h2>
            <Button asChild className="h-12 rounded-full px-8 font-semibold shadow-lg">
              <Link to="/feedback">Send Feedback</Link>
            </Button>
          </div>
        </RevealSection>
      )}

        <footer className="border-t border-border bg-secondary py-4 text-text-muted">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:items-center md:gap-6 lg:gap-10">
              <div className="min-w-0 text-left">
                <p className="text-xl font-bold text-text-primary">Assembly</p>
                <p className="mt-1 text-sm text-text-secondary">
                  {ASSEMBLY_TAGLINE}
                </p>
              </div>
              <p className="text-center text-sm md:px-2">
                © {new Date().getFullYear()} Assembly. All rights reserved.
              </p>
              <div className="flex min-w-0 justify-end">
                <ConnectWithUsBlock align="end" />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </main>
  );
}
